/**
 * Блок «Показатели СЭР» (redesign по макету «Цифровой паспорт»): таблица
 * из 4 показателей СЭР — иконка + название, колонки ИФО / Динамика /
 * объём (млрд тг на уровне области, млн тг — ниже, см. `amountUnit`).
 *
 * Источник значений — снаружи через `values` (страница берёт их из
 * basic_stats type='SER' ответа /public/general либо из hardData-мока);
 * нет значения → «—». Отрицательный ИФО подсвечивается красным.
 * Подпись периода в правом углу — `periodLabel`.
 *
 * Drill-through (US-02 FR-4.6): при переданном `kato` виджет спрашивает
 * `/public/general/indicator-detail` по каждому показателю (type='SER',
 * subtype — см. SER_SUBTYPE_BY_KEY). Есть первичные данные (`has_data`) —
 * строка становится кнопкой и раскрывает их таблицей под собой; нет —
 * строка неактивна (aria-disabled + подпись «нет первичных данных»).
 * Демо-КАТО (hardData) — `kato={null}`, drill-through выключен.
 */
import type { LucideIcon } from 'lucide-react';
import {
  ChevronDown,
  ChevronRight,
  Cog,
  Coins,
  Factory,
  TrendingDown,
  TrendingUp,
  Wheat,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { cn } from '../../../../lib/cn';
import { getIndicatorDetail } from '../../../../requests/public.request';
import type {
  IndicatorDetailResponse,
  IndicatorDetailRow,
} from '../../../../types/public';

export type SerTableKey =
  | 'investments'
  | 'industry'
  | 'manufacturing'
  | 'agriculture';

/** Ключ строки таблицы → subtype basic_stats (type='SER'), US-02 FR-4.4. */
export const SER_SUBTYPE_BY_KEY: Record<SerTableKey, string> = {
  investments: 'INVESTMENT',
  industry: 'INDUSTRIAL_PRODUCTION',
  manufacturing: 'MANUFACTURING_INDUSTRY',
  agriculture: 'AGRICULTURE',
};

export interface SerTableValue {
  /** ИФО, % — синяя колонка; отрицательное значение — красным. */
  ifo: number | null;
  /** Изменение к прошлому периоду, % — зелёная колонка с трендом. */
  change: number | null;
  /** Объём в тенге (млрд/млн — по amountUnit таблицы). */
  amountMln: number | null;
}

const ROWS: Array<{ key: SerTableKey; icon: LucideIcon }> = [
  { key: 'investments', icon: Coins },
  { key: 'industry', icon: Factory },
  { key: 'manufacturing', icon: Cog },
  { key: 'agriculture', icon: Wheat },
];

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

interface Props {
  /** Значения по отраслям; нет значения → «—» в ячейках строки. */
  values?: Partial<Record<SerTableKey, SerTableValue>>;
  /** Единица объёма: млрд тг (область, по умолчанию) или млн тг. */
  amountUnit?: 'mlrd' | 'mln';
  /**
   * КАТО для drill-through (FR-4.6): по нему запрашиваются первичные
   * данные показателей. null/undefined — drill-through выключен
   * (демо-режим hardData).
   */
  kato?: number | null;
}

export const SerTable = ({
  values = {},
  amountUnit = 'mlrd',
  kato = null,
}: Props) => {
  const { t } = useTranslation();

  // Первичные данные по показателям (FR-4.6): грузим один раз на КАТО —
  // has_data нужен сразу, чтобы неактивные строки не выглядели кнопками.
  const [details, setDetails] = useState<
    Partial<Record<SerTableKey, IndicatorDetailResponse>>
  >({});
  const [openKey, setOpenKey] = useState<SerTableKey | null>(null);

  useEffect(() => {
    setDetails({});
    setOpenKey(null);
    if (kato === null) return;
    let cancelled = false;
    for (const { key } of ROWS) {
      getIndicatorDetail(kato, 'SER', SER_SUBTYPE_BY_KEY[key])
        .then((detail) => {
          if (!cancelled) {
            setDetails((prev) => ({ ...prev, [key]: detail }));
          }
        })
        .catch(() => {
          // Отказ эндпоинта — строка просто остаётся неактивной.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [kato]);

  return (
    <Card className="h-full shadow-card">
      <CardHeader className="flex-row items-baseline justify-between space-y-0">
        <CardTitle>{t('digitalPassport.serTable.title')}</CardTitle>
        {kato !== null && (
          <span className="shrink-0 text-xs text-text-faint">
            {t('digitalPassport.serTable.detail.hint')}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <section data-testid="block-ser-table">
          <div className="flex items-center gap-2 border-b border-surface-border pb-2 text-sm text-text-faint">
            <span className="min-w-0 flex-1" />
            <span className="w-20 shrink-0 text-right">
              {t('digitalPassport.serTable.columnIfo')}
            </span>
            <span className="w-24 shrink-0 text-right">
              {t('digitalPassport.serTable.columnChange')}
            </span>
            <span className="w-24 shrink-0 text-right">
              {amountUnit === 'mlrd'
                ? t('digitalPassport.serTable.columnAmount')
                : t('digitalPassport.serTable.columnAmountMln')}
            </span>
          </div>

          <ul>
            {ROWS.map(({ key, icon: Icon }) => {
              const value = values[key];
              const detail = details[key];
              // Активна только строка с первичными данными (FR-4.6).
              const drillable = detail?.hasData === true;
              const isOpen = openKey === key;
              return (
                <li
                  key={key}
                  data-testid={`ser-table-row-${key}`}
                  className="border-b border-surface-border last:border-b-0"
                >
                <div
                  className={cn(
                    'flex items-center gap-2 py-3.5 text-sm',
                    drillable &&
                      'cursor-pointer rounded-md hover:bg-brand-50/60',
                    isOpen && 'bg-brand-50/60',
                  )}
                  {...(drillable
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        'aria-expanded': isOpen,
                        'data-testid': `ser-table-row-${key}-toggle`,
                        onClick: () => setOpenKey(isOpen ? null : key),
                        onKeyDown: (event: React.KeyboardEvent) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setOpenKey(isOpen ? null : key);
                          }
                        },
                      }
                    : kato !== null && {
                        'aria-disabled': true,
                        title: t('digitalPassport.serTable.detail.noData'),
                      })}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-3">
                    {drillable ? (
                      isOpen ? (
                        <ChevronDown className="size-4 shrink-0 text-brand-600" />
                      ) : (
                        <ChevronRight className="size-4 shrink-0 text-brand-600" />
                      )
                    ) : (
                      kato !== null && <span className="w-4 shrink-0" />
                    )}
                    <Icon
                      strokeWidth={1.7}
                      className="size-6 shrink-0 text-brand-500"
                    />
                    <span className="truncate font-semibold">
                      {t(`digitalPassport.serTable.rows.${key}`)}
                    </span>
                  </span>
                  <span
                    data-testid={`ser-table-row-${key}-ifo`}
                    className={cn(
                      'w-20 shrink-0 text-right font-bold tabular-nums',
                      (value?.ifo ?? 0) < 0
                        ? 'text-status-danger'
                        : 'text-chart-indigo',
                    )}
                  >
                    {value?.ifo != null ? `${fmt1(value.ifo)}%` : '—'}
                  </span>
                  <span
                    data-testid={`ser-table-row-${key}-change`}
                    className={cn(
                      'flex w-24 shrink-0 items-center justify-end gap-1 font-bold tabular-nums',
                      (value?.change ?? 0) < 0
                        ? 'text-status-danger'
                        : 'text-brand-600',
                    )}
                  >
                    {value?.change != null ? (
                      <>
                        {value.change < 0 ? (
                          <TrendingDown className="size-4 shrink-0" />
                        ) : (
                          <TrendingUp className="size-4 shrink-0" />
                        )}
                        {fmt1(value.change)}%
                      </>
                    ) : (
                      '—'
                    )}
                  </span>
                  <span
                    data-testid={`ser-table-row-${key}-amount`}
                    className="w-24 shrink-0 text-right font-extrabold tabular-nums"
                  >
                    {value?.amountMln != null ? fmt1(value.amountMln) : '—'}
                  </span>
                </div>

                {isOpen && detail && (
                  <IndicatorDetail
                    rows={detail.rows}
                    amountUnit={amountUnit}
                    testId={`ser-table-detail-${key}`}
                  />
                )}
                </li>
              );
            })}
          </ul>
        </section>
      </CardContent>
    </Card>
  );
};

/**
 * Первичные данные показателя (FR-4.6) — таблица записей из
 * `/general/indicator-detail`: прошлый/текущий год, прирост, изменение,
 * ИФО и место, примечание. Нет значения → «—».
 */
const IndicatorDetail = ({
  rows,
  amountUnit,
  testId,
}: {
  rows: IndicatorDetailRow[];
  amountUnit: 'mlrd' | 'mln';
  testId: string;
}) => {
  const { t } = useTranslation();
  const cell = (v: number | null): string => (v !== null ? fmt1(v) : '—');
  const amountLabel =
    amountUnit === 'mlrd'
      ? t('digitalPassport.serTable.columnAmount')
      : t('digitalPassport.serTable.columnAmountMln');

  const columns: Array<{
    key: string;
    label: string;
    value: (row: IndicatorDetailRow) => string;
  }> = [
    {
      key: 'previous',
      label: `${t('digitalPassport.serTable.detail.columnPrevious')}, ${amountLabel}`,
      value: (row) => cell(row.previousYearVal),
    },
    {
      key: 'current',
      label: `${t('digitalPassport.serTable.detail.columnCurrent')}, ${amountLabel}`,
      value: (row) => cell(row.currentYearVal),
    },
    {
      key: 'increase',
      label: t('digitalPassport.serTable.detail.columnIncrease'),
      value: (row) => cell(row.increaseVal),
    },
    {
      key: 'change',
      label: t('digitalPassport.serTable.detail.columnChange'),
      value: (row) =>
        row.changePct !== null ? `${fmt1(row.changePct)}%` : '—',
    },
    {
      key: 'ifo',
      label: t('digitalPassport.serTable.columnIfo'),
      value: (row) => (row.ifo !== null ? `${fmt1(row.ifo)}%` : '—'),
    },
    {
      key: 'place',
      label: t('digitalPassport.serTable.detail.columnPlace'),
      value: (row) => (row.ifoPlace !== null ? String(row.ifoPlace) : '—'),
    },
  ];

  return (
    <div
      data-testid={testId}
      className="mb-3 rounded-lg bg-surface-muted/60 p-3"
    >
      <p className="pb-2 text-xs font-bold uppercase tracking-wide text-text-faint">
        {t('digitalPassport.serTable.detail.title')}
      </p>
      <ul className="space-y-2">
        {rows.map((row, index) => (
          <li
            key={row.id ?? index}
            data-testid={`${testId}-row-${index}`}
            className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-surface-border pt-2 text-sm first:border-t-0 first:pt-0 sm:grid-cols-3"
          >
            {columns.map((column) => (
              <span key={column.key} className="flex flex-col">
                <span className="text-xs leading-snug text-text-muted">
                  {column.label}
                </span>
                <span className="font-bold tabular-nums">
                  {column.value(row)}
                </span>
              </span>
            ))}
            {row.additionalInfo !== null && (
              <span className="col-span-full flex flex-col">
                <span className="text-xs leading-snug text-text-muted">
                  {t('digitalPassport.serTable.detail.columnNote')}
                </span>
                <span className="leading-snug">{row.additionalInfo}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
